import { Component, inject, effect, signal, computed } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router, RouterModule } from '@angular/router'
import { concatMap } from 'rxjs'

import { Tool } from '@cyborg-monorepo/hunter/common'
import {
  TypographyComponent,
  BadgeComponent,
  TagComponent,
  ButtonComponent,
  BreadcrumbListComponent,
  BreadcrumbItem,
  SelectComponent,
  SegmentedProgressBarComponent,
} from '@hyperkit'

import { TuningAPIService } from '@hunt/services'
import { QueryViewService } from '@hunt/services/reader/query-view.service'

import { HuntEditorComponent } from '../../hunt/details/components/hunt-editor/hunt-editor.component'
import {
  buildQueryProgressSegments,
  countQueriesInTools,
} from '../helpers/query-status-helper'
import { HuntAPIService } from '../services/api/hunt-api.service'
import { HuntEditorService } from '../services/editor/hunt-edit.service'
import { NavigationService } from '../services/navigation.service'
import { HuntViewService } from '../services/reader/hunt-view.service'
import { PackageViewService } from '../services/reader/package-view.service'
import { SessionService } from '../services/session.service'

import { DefaultViewComponent, QuerySelectedViewComponent } from './components'
import { HuntQueryCardComponent } from './components/query-card/hunt-query-card.component'
import { QueryTuningComponent } from './components/tuning/query-tuning.component'

@Component({
  standalone: true,
  selector: 'cyborg-monorepo-hunt-package-details',
  imports: [
    RouterModule,
    FormsModule,
    TypographyComponent,
    BadgeComponent,
    TagComponent,
    ButtonComponent,
    BreadcrumbListComponent,
    SelectComponent,
    DefaultViewComponent,
    QuerySelectedViewComponent,
    HuntQueryCardComponent,
    HuntEditorComponent,
    QueryTuningComponent,
    SegmentedProgressBarComponent,
  ],
  templateUrl: './hunt-package-details.component.html',
  styleUrls: ['./hunt-package-details.component.scss'],
})
export class HuntPackageDetailsComponent {
  public readonly nav = inject(NavigationService)
  public readonly huntEdit = inject(HuntEditorService)
  public readonly pkgView = inject(PackageViewService)
  public readonly huntView = inject(HuntViewService)
  public readonly session = inject(SessionService)
  private readonly api = inject(HuntAPIService)
  private readonly router = inject(Router)
  public readonly queryView = inject(QueryViewService)
  public readonly tuningAPIService = inject(TuningAPIService)

  readonly environments = this.pkgView.getAllEnvironments
  readonly selectedEnvironment = this.pkgView.selectedEnvironment
  readonly activeTools = this.pkgView.getToolsBySelectedEnvironment
  readonly activeHuntPackage = this.pkgView.activePackage

  readonly selectedToolType = this.queryView.selectedToolType
  readonly selectedQuery = this.queryView.selectedQuery
  readonly isPivotQuery = this.queryView.isPivotQuery
  readonly breadcrumbs = signal<BreadcrumbItem[]>([])
  readonly showTuning = this.nav.showTuning

  readonly queryStatusSegments = computed(() =>
    buildQueryProgressSegments(countQueriesInTools(this.activeTools())),
  )

  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )

  constructor() {
    effect(() => {
      this.breadcrumbs.set([
        { text: 'Hunt', link: '/hunt/hunts' },
        {
          text: this.huntView.name(),
          link: `/hunt/${this.nav.huntId()}/workbench`,
        },
        {
          text: this.activeHuntPackage()?.title ?? 'Untitled Package',
          selected: true,
        },
      ])
    })

    effect(() => {
      const envs = this.environments()
      const sel = this.selectedEnvironment()
      if (!sel && envs.length > 0) {
        this.nav.setSelectedEnvironmentId(envs[0].id)
      }
    })
  }

  trackByTool(_idx: number, tool: Tool): string {
    return tool.id
  }

  navigateToOverview(): void {
    const pkg = this.activeHuntPackage()
    if (pkg) {
      this.nav.navigateToHuntPackage(pkg.UUID)
    }
  }

  /** Persist and "close" the hunt, then jump to the report editor */
  async closeHunt() {
    const hunt = this.session.activeHunt()
    if (!hunt) return
    this.api
      .updateHunt(hunt.id, hunt)
      .pipe(concatMap(() => this.api.closeHunt(hunt.id)))
      .subscribe(async (updated) => {
        await this.session.loadHunt(updated.id, true)
        this.router.navigate(['/hunt', updated.id, 'edit-report'])
      })
  }
}
